import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useRouter, useLocalSearchParams, Stack, useFocusEffect } from "expo-router";
import { useCallback, useMemo, useState } from "react";
import { StyleSheet, View, ScrollView, TouchableOpacity, ActivityIndicator } from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { useOnboardingRefresh } from "@/hooks/useOnboardingWebSocket";
import { MOBILE_DEBUG_VERSION } from "@/lib/debug";
import {
    fetchOnboardingState,
    type ObSectionNode,
    type ObSubsectionNode
} from "@/lib/onboarding-api";
import { getAttentionItems } from "@/lib/onboarding-attention";
import {
    computeSectionProgress,
    computeSubsectionProgress,
    isSectionLocked
} from "@/lib/onboarding-branching";
import { colors, fonts, radius, spacing, typography } from "@/lib/theme";

// ── Gradient title helper ───────────────────────────────────────────────────
function GradientTitle({ text }: { text: string }) {
    const words = text.split(" ");
    const chars = text.split("");
    const len = chars.length;
    const isLongTitle = text.length > 22;
    let colorIndex = 0;

    return (
        <View style={styles.titleRow}>
            {words.map((word, wordIndex) => {
                const letters = word.split("");
                return (
                    <View key={`${word}-${wordIndex}`} style={styles.titleWord}>
                        {letters.map((ch, charIndex) => {
                            const t = len > 1 ? colorIndex / (len - 1) : 0;
                            const r = Math.round(69 + t * (231 - 69));
                            const g = Math.round(210 + t * (72 - 210));
                            const b = Math.round(176 + t * (245 - 176));
                            const letter = (
                                <Text
                                    key={`${wordIndex}-${charIndex}`}
                                    style={[
                                        styles.sectionTitle,
                                        isLongTitle && styles.sectionTitleLong,
                                        { color: `rgb(${r},${g},${b})` }
                                    ]}
                                >
                                    {ch}
                                </Text>
                            );
                            colorIndex += 1;
                            return letter;
                        })}
                    </View>
                );
            })}
        </View>
    );
}

// ── Section progress bar ────────────────────────────────────────────────────
function SectionProgressBar({ approved, total }: { approved: number; total: number }) {
    const pct = total > 0 ? Math.round((approved / total) * 100) : 0;
    const remaining = total - approved;
    return (
        <View style={styles.progressWrap}>
            <View style={styles.progressTextRow}>
                <Text style={styles.progressLabel}>{remaining} items left</Text>
                <Text style={styles.progressPct}>{pct}%</Text>
            </View>
            <View style={styles.progressTrack}>
                <LinearGradient
                    colors={[colors.teal400, colors.pink400]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    style={[styles.progressFill, { width: `${pct}%` as any }]}
                />
            </View>
        </View>
    );
}

// ── Subsection Card ─────────────────────────────────────────────────────────
function SubsectionCard({
    subsection,
    onPress
}: {
    subsection: ObSubsectionNode;
    onPress: () => void;
}) {
    //  The shared count, not a local copy - see computeSubsectionProgress.
    const { approved, total } = computeSubsectionProgress(subsection);
    const isComplete = total > 0 && approved === total;
    const attentionItems = getAttentionItems(subsection.items);
    const pct = total > 0 ? Math.round((approved / total) * 100) : 0;

    return (
        <TouchableOpacity
            style={[styles.card, isComplete && styles.cardComplete]}
            activeOpacity={0.7}
            onPress={onPress}
        >
            <View style={styles.cardRow}>
                {/* Icon */}
                <View style={[styles.iconBox, isComplete && styles.iconBoxComplete]}>
                    <MaterialCommunityIcons
                        name={
                            (isComplete
                                ? "check-circle"
                                : "form-select") as keyof typeof MaterialCommunityIcons.glyphMap
                        }
                        size={22}
                        color={isComplete ? colors.success500 : colors.teal600}
                    />
                </View>

                {/* Name */}
                <View style={styles.cardInfo}>
                    <Text style={styles.cardName}>{subsection.name}</Text>
                </View>

                {/* Right: count or check + chevron */}
                <View style={styles.cardRight}>
                    {isComplete ? (
                        <View style={styles.completeBadge}>
                            <MaterialCommunityIcons name="check" size={16} color={colors.surface} />
                        </View>
                    ) : (
                        <Text style={styles.cardProgress}>
                            {approved}/{total}
                        </Text>
                    )}
                    <MaterialCommunityIcons name="chevron-right" size={22} color="#D1D5DB" />
                </View>
            </View>

            {/* Attention items as dot + label + status badge */}
            {attentionItems.length > 0 && (
                <View style={styles.attentionList}>
                    {attentionItems.map((a, i) => {
                        //  `waiting_on_you` is amber like pending, not red: the
                        //  document is fine, it simply cannot be checked yet.
                        //  Without this it fell through to the REJECTED arm and
                        //  told her a good selfie had been turned down.
                        const amber = a.type === "pending" || a.type === "waiting_on_you";
                        const red = a.type === "rejected" || a.type === "failed";
                        return (
                            <View key={i} style={styles.attentionItem}>
                                <View
                                    style={[
                                        styles.attentionDot,
                                        amber && { backgroundColor: colors.warning600 },
                                        red && { backgroundColor: colors.error500 }
                                    ]}
                                />
                                <View style={styles.attentionTextWrap}>
                                    <Text
                                        style={[
                                            styles.attentionName,
                                            amber && { color: colors.warning600 },
                                            red && { color: colors.error500 }
                                        ]}
                                    >
                                        {a.itemName}
                                    </Text>
                                    {a.detail ? (
                                        <Text style={styles.attentionDetail}>{a.detail}</Text>
                                    ) : null}
                                </View>
                                <View
                                    style={[
                                        styles.statusBadge,
                                        amber && styles.statusBadgePending,
                                        red && styles.statusBadgeRejected
                                    ]}
                                >
                                    <Text
                                        style={[
                                            styles.statusBadgeText,
                                            amber && { color: colors.warning600 },
                                            red && { color: colors.error500 }
                                        ]}
                                    >
                                        {a.type === "pending"
                                            ? "PENDING"
                                            : a.type === "waiting_on_you"
                                              ? "NEEDS YOUR ID"
                                              : "REJECTED"}
                                    </Text>
                                </View>
                            </View>
                        );
                    })}
                </View>
            )}

            {/* Progress bar — pink if issues, teal if clean */}
            {total > 0 && (
                <View style={styles.cardProgressTrack}>
                    <View
                        style={[
                            styles.cardProgressFill,
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            { width: `${pct}%` as any },
                            attentionItems.length > 0 && { backgroundColor: colors.pink400 },
                            attentionItems.length === 0 && { backgroundColor: colors.teal400 }
                        ]}
                    />
                </View>
            )}
        </TouchableOpacity>
    );
}

// ── Main Screen ─────────────────────────────────────────────────────────────
export default function SectionScreen() {
    const router = useRouter();
    const { sectionId, rnId } = useLocalSearchParams<{
        sectionId: string;
        rnId?: string;
    }>();
    const [section, setSection] = useState<ObSectionNode | null>(null);
    const [allSections, setAllSections] = useState<ObSectionNode[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [resolvedRnId, _setResolvedRnId] = useState(rnId ?? "");
    const onboardingHref = resolvedRnId ? `/onboarding?rnId=${resolvedRnId}` : "/onboarding";

    function refetch() {
        if (!sectionId || !resolvedRnId) {
            return;
        }
        setLoading(true);
        fetchOnboardingState(resolvedRnId)
            .then((state) => {
                const found = state.config.find((s) => String(s.id) === sectionId);
                setSection(found ?? null);
                setAllSections(state.config);
            })
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }

    useFocusEffect(
        useCallback(() => {
            refetch();
        }, [sectionId, resolvedRnId])
    );

    useOnboardingRefresh(resolvedRnId, refetch);

    // Compute overall section progress (shared with the home screen via
    // computeSectionProgress so the two never disagree).
    const sectionProgress = useMemo(() => {
        if (!section) {
            return { approved: 0, total: 0 };
        }
        return computeSectionProgress(section);
    }, [section]);

    if (loading) {
        return (
            <View style={styles.center}>
                <ActivityIndicator size="large" color={colors.teal400} />
            </View>
        );
    }

    if (error || !section) {
        return (
            <View style={styles.center}>
                <MaterialCommunityIcons
                    name="alert-circle-outline"
                    size={48}
                    color={colors.error500}
                />
                <Text style={styles.errorText}>{error ?? "Section not found"}</Text>
            </View>
        );
    }

    // Locked section (e.g. Direct Deposit / W9 / Contract) — not enterable until
    // every prior required section is complete. Guard here too, in case it's
    // reached by a direct link rather than a tap on the (disabled) home card.
    if (isSectionLocked(section, allSections)) {
        return (
            <View style={styles.center}>
                <MaterialCommunityIcons
                    name="lock-outline"
                    size={48}
                    color={colors.textSecondary}
                />
                <Text style={styles.errorText}>
                    Complete the earlier sections first to unlock {section.name}.
                </Text>
                <TouchableOpacity
                    style={styles.lockedBackBtn}
                    onPress={() => router.navigate(onboardingHref)}
                >
                    <Text style={styles.lockedBackText}>Back to onboarding</Text>
                </TouchableOpacity>
            </View>
        );
    }

    return (
        <>
            <Stack.Screen options={{ headerShown: false }} />
            <SafeAreaView style={styles.safeArea} edges={["top", "bottom"]}>
                <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
                    <View style={styles.topBar}>
                        <TouchableOpacity
                            style={styles.backArrow}
                            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                            onPress={() => {
                                console.info(
                                    `[mobile ${MOBILE_DEBUG_VERSION}] section back pressed`,
                                    {
                                        sectionId,
                                        onboardingHref
                                    }
                                );
                                router.navigate(onboardingHref);
                            }}
                        >
                            <MaterialCommunityIcons
                                name="chevron-left"
                                size={28}
                                color={colors.textPrimary}
                            />
                        </TouchableOpacity>
                        <View style={styles.topBarSpacer} />
                    </View>

                    {/* Section header */}
                    <View style={styles.sectionHeader}>
                        <Text style={styles.sectionLabel}>SECTION</Text>
                        <GradientTitle text={section.name} />
                        {section.description && (
                            <Text style={styles.sectionDesc}>{section.description}</Text>
                        )}
                    </View>

                    {/* Section progress */}
                    <SectionProgressBar
                        approved={sectionProgress.approved}
                        total={sectionProgress.total}
                    />

                    {/* Subsection cards */}
                    {section.subsections.map((sub) => {
                        const isIdentityVerification = sub.items.some(
                            (item) => item.aiConfig?.verificationMethod === "aws_face"
                        );
                        return (
                            <SubsectionCard
                                key={sub.id}
                                subsection={sub}
                                onPress={() => {
                                    if (isIdentityVerification) {
                                        router.push(
                                            `/onboarding/identity-verification?rnId=${resolvedRnId}&sectionId=${sectionId}&subsectionId=${sub.id}`
                                        );
                                    } else {
                                        router.push(
                                            `/onboarding/subsection/${sub.id}?rnId=${resolvedRnId}&sectionId=${sectionId}`
                                        );
                                    }
                                }}
                            />
                        );
                    })}
                </ScrollView>
            </SafeAreaView>
        </>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.background
    },
    safeArea: {
        flex: 1,
        backgroundColor: colors.background
    },
    scrollContent: {
        padding: spacing.base + 8,
        paddingBottom: 40,
        gap: 14
    },
    center: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
        gap: spacing.md,
        padding: spacing.xl
    },
    errorText: {
        ...typography.body,
        color: colors.error500,
        textAlign: "center"
    },
    lockedBackBtn: {
        marginTop: 8,
        paddingVertical: 10,
        paddingHorizontal: 20,
        borderRadius: 12,
        backgroundColor: colors.teal600
    },
    lockedBackText: {
        ...typography.body,
        color: colors.surface,
        fontFamily: fonts.semiBold
    },

    // ── Top bar ──
    topBar: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 8
    },
    backArrow: {
        width: 44,
        height: 44,
        borderRadius: 12,
        alignItems: "center",
        justifyContent: "center",
        marginLeft: -10
    },
    topBarSpacer: {
        width: 34
    },

    // ── Section header ──
    sectionHeader: {
        paddingTop: spacing.sm,
        paddingBottom: spacing.xs,
        gap: 4,
        marginBottom: 8
    },
    sectionLabel: {
        fontSize: 10,
        fontFamily: fonts.bold,
        color: colors.textTertiary,
        letterSpacing: 2,
        textTransform: "uppercase"
    },
    titleRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        columnGap: 10
    },
    titleWord: {
        flexDirection: "row"
    },
    sectionTitle: {
        fontSize: 34,
        fontFamily: fonts.extraBold,
        letterSpacing: -0.5,
        color: colors.teal400,
        lineHeight: 40
    },
    sectionTitleLong: {
        fontSize: 30,
        lineHeight: 36
    },
    sectionDesc: {
        fontSize: 14,
        fontFamily: fonts.regular,
        color: "#6B7280",
        marginTop: 2,
        lineHeight: 20
    },

    // ── Section progress ──
    progressWrap: {
        gap: spacing.sm,
        marginBottom: 8
    },
    progressTextRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center"
    },
    progressLabel: {
        fontSize: 13,
        fontFamily: fonts.semiBold,
        color: "#374151"
    },
    progressPct: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.teal400
    },
    progressTrack: {
        height: 6,
        borderRadius: 100,
        backgroundColor: "#E5E7EB",
        overflow: "hidden"
    },
    progressFill: {
        height: "100%",
        borderRadius: 100
    },

    // ── Card ──
    card: {
        backgroundColor: "#FAFAFA",
        borderRadius: 16,
        borderWidth: 1,
        borderColor: "#F0F0F0",
        padding: 18
    },
    cardComplete: {
        borderColor: colors.success400
    },
    cardRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 14
    },
    iconBox: {
        width: 48,
        height: 48,
        borderRadius: 16,
        backgroundColor: "#E8FAF5",
        borderWidth: 1.5,
        borderColor: colors.border,
        alignItems: "center",
        justifyContent: "center"
    },
    iconBoxComplete: {
        backgroundColor: colors.success50,
        borderColor: colors.success400
    },
    cardInfo: {
        flex: 1,
        gap: 2
    },
    cardName: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: "#1F2937"
    },
    cardRight: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8
    },
    cardProgress: {
        fontSize: 14,
        fontFamily: fonts.semiBold,
        color: "#9CA3AF"
    },
    completeBadge: {
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: colors.success500,
        alignItems: "center",
        justifyContent: "center"
    },

    // ── Attention items ──
    attentionList: {
        marginTop: 10,
        gap: 4
    },
    attentionItem: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6
    },
    attentionDot: {
        width: 6,
        height: 6,
        borderRadius: 3,
        backgroundColor: colors.pink400
    },
    attentionTextWrap: {
        flex: 1
    },
    attentionName: {
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.pink400
    },
    attentionDetail: {
        fontSize: 11,
        fontFamily: fonts.regular,
        color: colors.textSecondary,
        marginTop: 2
    },
    statusBadge: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: radius.full
    },
    statusBadgePending: {
        backgroundColor: "rgba(245, 158, 11, 0.1)"
    },
    statusBadgeRejected: {
        backgroundColor: "rgba(239, 68, 68, 0.1)"
    },
    statusBadgeText: {
        fontSize: 10,
        fontFamily: fonts.bold,
        letterSpacing: 0.3
    },

    // ── Card progress bar ──
    cardProgressTrack: {
        height: 4,
        borderRadius: 100,
        backgroundColor: "#E5E7EB",
        marginTop: 12,
        overflow: "hidden"
    },
    cardProgressFill: {
        height: "100%",
        borderRadius: 100
    }
});
